/**
 * Shared helpers for normalizing permission payloads from auth/me.
 * Permissions may arrive as strings or objects with `title` / `name`.
 */

export type PermissionLike =
  | string
  | {
      id?: string | number;
      title?: string;
      name?: string;
      module?: string;
      action?: string;
    };

export interface NormalizedPermission {
  id: string;
  title: string;
  module: string;
  action: string;
}

export const PERMISSION_CACHE_KEY = 'awfar_user_permissions';

export const getPermissionTitle = (
  permission: PermissionLike | null | undefined
): string | null => {
  if (permission == null) return null;
  if (typeof permission === 'string') {
    const trimmed = permission.trim();
    return trimmed || null;
  }
  const title =
    (typeof permission.title === 'string' && permission.title.trim()) ||
    (typeof permission.name === 'string' && permission.name.trim()) ||
    '';
  return title || null;
};

export const normalizePermission = (
  permission: PermissionLike
): NormalizedPermission | null => {
  const title = getPermissionTitle(permission);
  if (!title) return null;

  if (typeof permission === 'string') {
    return { id: title, title, module: '', action: '' };
  }

  return {
    id: String(permission.id ?? title),
    title,
    module: typeof permission.module === 'string' ? permission.module : '',
    action: typeof permission.action === 'string' ? permission.action : '',
  };
};

/**
 * Build the effective granted permission list from auth/me-style userInfo.
 * Prefers top-level `permissions` when present (API effective set); otherwise
 * flattens nested `roles[].permissions`.
 */
export const flattenUserPermissions = (
  userInfo: any
): NormalizedPermission[] => {
  if (!userInfo) return [];

  const collected: PermissionLike[] = [];
  const direct = userInfo.permissions;

  if (Array.isArray(direct) && direct.length > 0) {
    collected.push(...direct);
  } else if (Array.isArray(userInfo.roles)) {
    for (const role of userInfo.roles) {
      if (Array.isArray(role?.permissions)) {
        collected.push(...role.permissions);
      }
    }
  }

  const normalized = collected
    .map(normalizePermission)
    .filter(Boolean) as NormalizedPermission[];

  return normalized.filter(
    (permission, index, self) =>
      index === self.findIndex((p) => p.title === permission.title)
  );
};

export const clearPermissionCache = (): void => {
  try {
    localStorage.removeItem(PERMISSION_CACHE_KEY);
  } catch {
    // ignore storage errors
  }
};
